import Phaser from 'phaser';
import { GAME } from '../constants';
import { RunData, removeCurse } from '../run/RunState';
import { CardModifier } from '../types';
import { getCurseById } from '../cards/CurseRegistry';
import { PIXEL_THEME, FONT_FAMILY, createPixelPanel, drawPixelBackdrop } from '../ui/PixelTheme';
import { fadeIn, fadeOut } from '../ui/SceneTransitions';
import { createAmbientMotes } from '../ui/ParticleEffects';

export class RestScene extends Phaser.Scene {
  private chosen = false;
  private purgeElements: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super({ key: 'RestScene' });
  }

  create(data: { run: RunData }): void {
    fadeIn(this);
    this.chosen = false;
    this.purgeElements = [];
    const { run } = data;
    run.curses = run.curses ?? [];
    const cx = GAME.WIDTH / 2;
    const cy = GAME.HEIGHT / 2;

    drawPixelBackdrop(this);

    const headerPanel = createPixelPanel(this, cx, 56, GAME.WIDTH - 110, 92, {
      fill: 0x201724,
      stroke: 0xc56c43,
      accent: 0xffd38c,
    });
    headerPanel.elements.forEach(el => el.setDepth(1));

    const statusPanel = createPixelPanel(this, cx, 122, GAME.WIDTH - 220, 40, {
      fill: 0x132038,
      stroke: 0x4f7dab,
      accent: 0x95d6f2,
    });
    statusPanel.elements.forEach(el => el.setDepth(1));

    this.add.text(cx, 50, 'CAMPFIRE', {
      fontSize: '18px',
      fontFamily: FONT_FAMILY,
      color: '#ffd7a0',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(2);

    this.add.text(cx, 82, 'Recover, purge, or upgrade before the next fight.', {
      fontSize: '8px',
      fontFamily: FONT_FAMILY,
      color: '#d2ddf5',
    }).setOrigin(0.5).setDepth(2);

    this.add.text(cx, 122, `HP ${run.playerHp}/${run.playerMaxHp}  |  Deck ${run.deckNumbers.length}  |  Curses ${run.curses.length}  |  Modded cards ${run.modifiers.size}`, {
      fontSize: '11px',
      fontFamily: FONT_FAMILY,
      color: PIXEL_THEME.textMuted,
    }).setOrigin(0.5).setDepth(2);

    // Pixel-art campfire with particle effects
    const fireY = cy - 78;

    // Warm glow overlay
    const glowOuter = this.add.circle(cx, fireY, 72, 0xff6d2a, 0.1).setDepth(0);
    const glowInner = this.add.circle(cx, fireY, 44, 0xff932e, 0.18).setDepth(0);
    this.tweens.add({
      targets: glowOuter,
      scale: 1.15,
      alpha: 0.05,
      duration: 1400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: glowInner,
      scale: 1.1,
      alpha: 0.12,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Pixel-art campfire (12x12 grid)
    this.drawPixelCampfire(cx, fireY);

    // Background floating motes
    createAmbientMotes(this, 5);

    // Fire spark particles
    for (let i = 0; i < 6; i++) {
      const spark = this.add.circle(
        cx + Phaser.Math.Between(-14, 14),
        fireY + Phaser.Math.Between(-18, 4),
        Phaser.Math.Between(1, 3),
        Phaser.Math.RND.pick([0xffc46a, 0xff932e, 0xffdd88]),
        0.7,
      ).setDepth(4);
      this.tweens.add({
        targets: spark,
        y: fireY - Phaser.Math.Between(30, 60),
        alpha: 0,
        scale: 0.3,
        duration: Phaser.Math.Between(600, 1200),
        delay: Phaser.Math.Between(0, 800),
        repeat: -1,
        onRepeat: () => {
          spark.setPosition(
            cx + Phaser.Math.Between(-14, 14),
            fireY + Phaser.Math.Between(-10, 4),
          );
          spark.setAlpha(0.7);
          spark.setScale(1);
        },
      });
    }

    const buttonY = cy + 96;
    const spacing = 230;

    // Option 1: Rest (heal 30%)
    const healAmount = Math.floor(run.playerMaxHp * 0.3);
    const actualHeal = Math.min(healAmount, run.playerMaxHp - run.playerHp);
    const healBtn = this.createChoice(
      cx - spacing, buttonY, 'REST',
      actualHeal > 0 ? `Heal ${healAmount} HP` : 'Already full HP',
      '#66cc66',
    );
    healBtn.on('pointerdown', () => {
      if (this.chosen) return;
      this.chosen = true;
      const healed = Math.min(healAmount, run.playerMaxHp - run.playerHp);
      run.playerHp += healed;
      this.showResult(`Healed ${healed} HP! (${run.playerHp}/${run.playerMaxHp})`, '#66cc66', run);
    });

    // Option 2: Purge a card or curse (player chooses)
    if (run.deckNumbers.length > 5 || run.curses.length > 0) {
      const removeBtn = this.createChoice(
        cx, buttonY, 'PURGE',
        'Remove a card or a curse',
        '#ff6666',
      );
      removeBtn.on('pointerdown', () => {
        if (this.chosen) return;
        this.chosen = true;
        this.showPurgeSelection(run);
      });
    }

    // Option 3: Upgrade a modifier
    const modifiedCards = Array.from(run.modifiers.entries());
    if (modifiedCards.length > 0) {
      const upgradeBtn = this.createChoice(
        cx + spacing, buttonY, 'UPGRADE',
        'Boost a modifier +50%',
        '#ffaa00',
      );
      upgradeBtn.on('pointerdown', () => {
        if (this.chosen) return;
        this.chosen = true;
        this.showUpgradeSelection(run, modifiedCards);
      });
    }
  }

  private showPurgeSelection(run: RunData): void {
    const cx = GAME.WIDTH / 2;
    const sorted = [...run.deckNumbers].sort((a, b) => a - b);
    const overlayPanel = createPixelPanel(this, cx, GAME.HEIGHT / 2 + 112, GAME.WIDTH - 120, 348, {
      fill: 0x11172b,
      stroke: 0xbf6944,
      accent: 0xffd58f,
      alpha: 0.96,
    });
    overlayPanel.elements.forEach(el => el.setDepth(50));
    overlayPanel.bg.setInteractive();
    this.purgeElements.push(...overlayPanel.elements);

    const title = this.add.text(cx, GAME.HEIGHT / 2 - 36, 'PURGE - choose one card or curse', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#ffd2a1',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(51);
    this.purgeElements.push(title);

    const cols = Math.min(Math.max(sorted.length, 1), 8);
    const spacing = 96;
    const startX = cx - (cols - 1) * spacing / 2;
    const y = GAME.HEIGHT / 2 + 6;

    for (let i = 0; i < sorted.length; i++) {
      const num = sorted[i];
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * spacing;
      const cardY = y + row * 44;

      const mod = run.modifiers.get(num);
      const color = num < 0 ? '#ff6666' : mod ? '#ffdd66' : '#ffffff';
      let label = `${num}`;
      if (mod) label += ' [M]';

      const btn = this.add.text(x, cardY, label, {
        fontSize: '13px',
        fontFamily: FONT_FAMILY,
        color,
        backgroundColor: '#2d2230',
        padding: { x: 8, y: 5 },
      }).setOrigin(0.5).setDepth(51).setInteractive({ useHandCursor: true });

      btn.on('pointerover', () => btn.setBackgroundColor('#4a3140'));
      btn.on('pointerout', () => btn.setBackgroundColor('#2d2230'));
      btn.on('pointerdown', () => {
        this.removeSingleCard(run, num);
        for (const el of this.purgeElements) el.destroy();
        this.showResult(`Removed card ${num} from deck! (${run.deckNumbers.length} remaining)`, '#ff6666', run);
      });

      this.purgeElements.push(btn);
    }

    const curseStartY = y + Math.ceil(sorted.length / Math.max(cols, 1)) * 50 + 28;
    if (run.curses.length > 0) {
      const curseTitle = this.add.text(cx, curseStartY - 18, 'CURSES', {
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
        color: '#ff8899',
      }).setOrigin(0.5).setDepth(51);
      this.purgeElements.push(curseTitle);

      const curseCols = Math.min(run.curses.length, 4);
      const curseSpacing = 180;
      const curseStartX = cx - (curseCols - 1) * curseSpacing / 2;
      for (let i = 0; i < run.curses.length; i++) {
        const curseId = run.curses[i];
        const curse = getCurseById(curseId);
        const row = Math.floor(i / curseCols);
        const col = i % curseCols;
        const x = curseStartX + col * curseSpacing;
        const cy = curseStartY + row * 46;

        const curseBtn = this.add.text(x, cy, curse?.name ?? curseId, {
          fontSize: '12px',
          fontFamily: FONT_FAMILY,
          color: '#ffb1bd',
          backgroundColor: '#3a2230',
          padding: { x: 8, y: 5 },
        }).setOrigin(0.5).setDepth(51).setInteractive({ useHandCursor: true });

        curseBtn.on('pointerover', () => curseBtn.setBackgroundColor('#5d3143'));
        curseBtn.on('pointerout', () => curseBtn.setBackgroundColor('#3a2230'));
        curseBtn.on('pointerdown', () => {
          const removed = removeCurse(run, i);
          for (const el of this.purgeElements) el.destroy();
          this.showResult(`Purged curse ${curse?.name ?? removed ?? curseId}.`, '#ff8899', run);
        });

        this.purgeElements.push(curseBtn);
      }
    }

    // Cancel button
    const cancelY = run.curses.length > 0
      ? curseStartY + Math.ceil(run.curses.length / Math.max(Math.min(run.curses.length, 4), 1)) * 46 + 8
      : y + Math.ceil(sorted.length / Math.max(cols, 1)) * 50 + 10;
    const cancelBtn = this.add.text(cx, cancelY, '[ CANCEL ]', {
      fontSize: '14px',
      fontFamily: FONT_FAMILY,
      color: '#ffd6a2',
      backgroundColor: '#2a2e47',
      padding: { x: 10, y: 6 },
    }).setOrigin(0.5).setDepth(52).setInteractive({ useHandCursor: true });
    cancelBtn.on('pointerdown', () => {
      for (const el of this.purgeElements) el.destroy();
      this.purgeElements = [];
      this.chosen = false;
    });
    this.purgeElements.push(cancelBtn);
  }

  private showUpgradeSelection(run: RunData, modifiedCards: [number, CardModifier][]): void {
    const cx = GAME.WIDTH / 2;
    const overlayPanel = createPixelPanel(this, cx, GAME.HEIGHT / 2 + 112, GAME.WIDTH - 120, 324, {
      fill: 0x11192a,
      stroke: 0xbd7a45,
      accent: 0xffd78c,
      alpha: 0.96,
    });
    overlayPanel.elements.forEach(el => el.setDepth(50));
    overlayPanel.bg.setInteractive();
    this.purgeElements.push(...overlayPanel.elements);

    const title = this.add.text(cx, GAME.HEIGHT / 2 - 36, 'UPGRADE - choose one modified card', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#ffd39a',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(51);
    this.purgeElements.push(title);

    const cols = Math.min(modifiedCards.length, 4);
    const spacing = 196;
    const startX = cx - (cols - 1) * spacing / 2;
    const y = GAME.HEIGHT / 2 + 24;

    for (let i = 0; i < modifiedCards.length; i++) {
      const [cardNum, mod] = modifiedCards[i];
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * spacing;
      const rowY = y + row * 66;

      const label = `${cardNum} (+${mod.effect.value})`;
      const btn = this.add.text(x, rowY, label, {
        fontSize: '14px',
        fontFamily: FONT_FAMILY,
        color: '#ffdd9a',
        backgroundColor: '#312d24',
        padding: { x: 11, y: 7 },
      }).setOrigin(0.5).setDepth(51).setInteractive({ useHandCursor: true });

      btn.on('pointerover', () => btn.setBackgroundColor('#57503b'));
      btn.on('pointerout', () => btn.setBackgroundColor('#312d24'));
      btn.on('pointerdown', () => {
        const oldVal = mod.effect.value;
        mod.effect.value = Math.ceil(oldVal * 1.5);
        run.modifiers.set(cardNum, mod);
        for (const el of this.purgeElements) el.destroy();
        this.showResult(`Card ${cardNum}'s modifier: ${oldVal} -> ${mod.effect.value}!`, '#ffaa00', run);
      });

      this.purgeElements.push(btn);
    }

    const cancelY = y + Math.ceil(modifiedCards.length / Math.max(cols, 1)) * 66 + 10;
    const cancelBtn = this.add.text(cx, cancelY, '[ CANCEL ]', {
      fontSize: '14px',
      fontFamily: FONT_FAMILY,
      color: '#ffd6a2',
      backgroundColor: '#2a2e47',
      padding: { x: 10, y: 6 },
    }).setOrigin(0.5).setDepth(52).setInteractive({ useHandCursor: true });
    cancelBtn.on('pointerdown', () => {
      for (const el of this.purgeElements) el.destroy();
      this.purgeElements = [];
      this.chosen = false;
    });
    this.purgeElements.push(cancelBtn);
  }

  private removeSingleCard(run: RunData, cardNumber: number): void {
    const index = run.deckNumbers.indexOf(cardNumber);
    if (index === -1) return;
    run.deckNumbers.splice(index, 1);
    if (!run.deckNumbers.includes(cardNumber)) {
      run.modifiers.delete(cardNumber);
    }
  }

  private createChoice(x: number, y: number, title: string, desc: string, color: string): Phaser.GameObjects.Rectangle {
    const panel = createPixelPanel(this, x, y, 208, 128, {
      fill: 0x1b2438,
      stroke: 0x5779a4,
      accent: 0x9fbcf1,
    });
    panel.elements.forEach(el => el.setDepth(2));
    const bg = panel.bg
      .setDepth(3)
      .setInteractive({ useHandCursor: true });

    this.add.text(x, y - 18, title, {
      fontSize: '12px',
      fontFamily: FONT_FAMILY,
      color,
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(4);

    this.add.text(x, y + 14, desc, {
      fontSize: '11px',
      fontFamily: FONT_FAMILY,
      color: '#cbd7ef',
      align: 'center',
      wordWrap: { width: 180 },
    }).setOrigin(0.5).setDepth(4);

    bg.on('pointerover', () => bg.setStrokeStyle(3, 0xffdf9f, 1));
    bg.on('pointerout', () => bg.setStrokeStyle(2, 0x5779a4, 1));

    return bg;
  }

  private showResult(text: string, color: string, run: RunData): void {
    const cx = GAME.WIDTH / 2;
    const resultPanel = createPixelPanel(this, cx, GAME.HEIGHT - 66, GAME.WIDTH - 180, 82, {
      fill: 0x1d2133,
      stroke: 0xc98253,
      accent: 0xffd290,
    });
    resultPanel.elements.forEach(el => el.setDepth(30));
    this.add.text(cx, GAME.HEIGHT - 78, text, {
      fontSize: '9px',
      fontFamily: FONT_FAMILY,
      color,
      fontStyle: 'bold',
      align: 'center',
      wordWrap: { width: GAME.WIDTH - 240 },
    }).setOrigin(0.5).setDepth(31);
    this.add.text(cx, GAME.HEIGHT - 50, 'Returning to map...', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#9bb2dc',
    }).setOrigin(0.5).setDepth(31);

    this.time.delayedCall(1300, () => {
      fadeOut(this).then(() => this.scene.start('MapScene', { run }));
    });
  }

  private drawPixelCampfire(cx: number, cy: number): void {
    // 12x12 pixel-art campfire
    const _ = null;
    const R = 0xff4422; // red flame
    const O = 0xff8833; // orange flame
    const Y = 0xffcc44; // yellow core
    const W = 0xffeeaa; // white hot
    const B = 0x664422; // wood brown
    const D = 0x443311; // dark wood

    const grid: (number | null)[][] = [
      [_, _, _, _, _, Y, _, _, _, _, _, _],
      [_, _, _, _, Y, W, Y, _, _, _, _, _],
      [_, _, _, O, Y, W, Y, O, _, _, _, _],
      [_, _, O, O, Y, W, Y, O, O, _, _, _],
      [_, _, O, R, O, Y, O, R, O, _, _, _],
      [_, R, R, R, O, O, O, R, R, R, _, _],
      [_, R, R, R, R, O, R, R, R, R, _, _],
      [_, _, R, R, R, R, R, R, R, _, _, _],
      [_, _, _, R, R, R, R, R, _, _, _, _],
      [_, _, D, B, D, B, D, B, D, _, _, _],
      [_, D, B, D, B, D, B, D, B, D, _, _],
      [_, _, D, _, D, _, _, D, _, D, _, _],
    ];

    const pixelSize = 4;
    const gridW = grid[0].length;
    const gridH = grid.length;
    const offsetX = (gridW * pixelSize) / 2;
    const offsetY = (gridH * pixelSize) / 2;

    const g = this.add.graphics().setDepth(3);
    for (let row = 0; row < gridH; row++) {
      for (let col = 0; col < gridW; col++) {
        const c = grid[row][col];
        if (c !== null) {
          g.fillStyle(c, 1);
          g.fillRect(
            cx - offsetX + col * pixelSize,
            cy - offsetY + row * pixelSize,
            pixelSize,
            pixelSize,
          );
        }
      }
    }

    // Animate flame flicker by toggling a few pixels
    this.time.addEvent({
      delay: 300,
      loop: true,
      callback: () => {
        g.clear();
        for (let row = 0; row < gridH; row++) {
          for (let col = 0; col < gridW; col++) {
            let c = grid[row][col];
            if (c !== null) {
              // Flicker top flame pixels
              if (row < 4 && c !== B && c !== D && Math.random() > 0.6) {
                c = Math.random() > 0.5 ? Y : O;
              }
              g.fillStyle(c, 1);
              g.fillRect(
                cx - offsetX + col * pixelSize,
                cy - offsetY + row * pixelSize,
                pixelSize,
                pixelSize,
              );
            }
          }
        }
      },
    });
  }
}
